import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  CardRarity,
  FixtureState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelM, Title3 } from '@sorare/core/src/atoms/typography';
import ScarcityBall from '@sorare/core/src/components/card/ScarcityBall';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerRow } from './PlayerRow';
import { RewardPoolFilter } from './RewardPoolFilter';
import { RewardPool_PlayerInterface } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  gap: calc(3 * var(--unit));

  a {
    color: var(--c-neutral-1000);
  }
`;

const Loading = styled.div`
  margin: var(--quadruple-unit) 0 calc(8 * var(--unit));
`;

const Filters = styled.div`
  display: flex;
  gap: var(--unit);
`;

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: var(--unit) 0;

  & > * {
    padding: var(--unit) var(--double-unit);
    flex-basis: 100%;

    @media ${tabletAndAbove} {
      flex-basis: 33%;
    }
  }
`;

const rarities = ['common', 'limited', 'rare', 'super_rare', 'unique'] as const;

const scarcitiesOptions = [
  ...rarities.map(scarcity => ({
    label: <ScarcityBall scarcity={scarcity} />,
    value: scarcity,
  })),
];

type RewardPoolFilter = {
  scarcity: CardRarity;
  tier: string;
};

export const useRewardPoolFilters = () => {
  const [filters, setFilters] = useQueryState<RewardPoolFilter>({
    scarcity: CardRarity.common,
    tier: '1',
  });

  const onFilterChange = useCallback(
    (changedFilter: Partial<RewardPoolFilter>) => {
      setFilters({ ...filters, ...changedFilter });
    },
    [filters, setFilters]
  );

  return [filters, onFilterChange] as const;
};

const DefaultTitle = ({ fixtureState }: Pick<Props, 'fixtureState'>) => {
  return (
    <>
      <Title3>
        <FormattedMessage id="RewardPool.title" defaultMessage="Rewards" />
      </Title3>
      {fixtureState !== FixtureState.closed && (
        <LabelM>
          <FormattedMessage
            id="RewardPool.subtitle"
            defaultMessage="Players are eligible to change tiers until fixture closing."
          />
        </LabelM>
      )}
    </>
  );
};

type Props = {
  loading?: boolean;
  playerRoute: string;
  fixtureState?: FixtureState | null;
  players?: RewardPool_PlayerInterface[] | null;
  filters: RewardPoolFilter;
  title?: ReactNode;
  onFilterChange: (change: Partial<RewardPoolFilter>) => void;
};

export const RewardPool = ({
  loading,
  playerRoute,
  fixtureState,
  players,
  filters,
  title,
  onFilterChange,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const tierOptions = ['1', '2', '3', '4', '5'].map(tier => ({
    value: tier,
    label: formatMessage(
      {
        id: 'Rewards.Filters.Tier',
        defaultMessage: 'Tier {tier}',
      },
      { tier }
    ),
  }));

  return (
    <Wrapper>
      {title !== undefined ? (
        title
      ) : (
        <DefaultTitle fixtureState={fixtureState} />
      )}
      <Filters>
        <RewardPoolFilter
          selected={filters.scarcity}
          options={scarcitiesOptions}
          onChange={scarcity => onFilterChange({ scarcity })}
        />
        <RewardPoolFilter
          selected={filters.tier}
          options={tierOptions}
          onChange={tier => onFilterChange({ tier })}
        />
      </Filters>

      {!players || loading ? (
        <Loading>
          <LoadingIndicator />
        </Loading>
      ) : (
        <List>
          {players.length ? (
            players.map(player => {
              return (
                <li key={player.slug}>
                  <PlayerRow
                    route={playerRoute}
                    playerSlug={player.slug}
                    playerName={player.displayName}
                    playerPicture={player.avatarPictureUrl}
                    teamName={player.team?.fullName ?? ''}
                  />
                </li>
              );
            })
          ) : (
            <FormattedMessage
              id="RewardPool.NoCardsAvailable"
              defaultMessage="No Cards available"
            />
          )}
        </List>
      )}
    </Wrapper>
  );
};

RewardPool.fragments = {
  PlayerInterface: gql`
    fragment RewardPool_PlayerInterface on PlayerInterface {
      slug
      avatarPictureUrl
      displayName
      team {
        slug
        fullName
      }
    }
  ` as TypedDocumentNode<RewardPool_PlayerInterface>,
};
